import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Switch } from "antd";
import Title from "antd/es/typography/Title";
import { LockOutlined, MenuOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { checkboxData } from "../../screens/History/HistoryUtils";

// Columns that must always stay visible, so their toggle is locked (disabled).
const COLUMN_MANAGER_LOCKED = ["spec_type", "card_no"];

// Stable identity for the default frozen list -- it feeds a useEffect dep array,
// and an inline [] default would re-run that effect on every render, wiping any
// toggles made while the modal is open.
const NO_FROZEN_COLUMNS = [];

function FrozenBadge({ frozen }) {
  if (!frozen) return null;

  return (
    <span className="field-item-frozen-badge" title="Frozen column">
      <LockOutlined />
      Frozen
    </span>
  );
}

function SortableRow({ item, onToggle }) {
  return (
    <div className={"field-item field-item-active"}>
      <button
        aria-label={`Drag handle for ${item.label}`}
        className="field-item-grip-btn"
        type="button"
      >
        <MenuOutlined
          style={{
            touchAction: "none",
            cursor: "move",
          }}
        />
      </button>
      <p className="field-item-label">{item.label}</p>
      <FrozenBadge frozen={item.frozen} />
      <Switch
        size="small"
        id={`sw-${item.id}`}
        checked={item.rank !== null}
        onChange={() => onToggle(item.id)}
        className="field-item-switch"
        disabled={COLUMN_MANAGER_LOCKED.includes(item.id) || item.frozen}
      />
    </div>
  );
}

// Memoized: a toggle or reorder only changes the rows it touches, so the rest
// keep their item identity and skip re-rendering.
const SortableItem = memo(function SortableItem({ item, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: item.rank === null });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.4 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="field-item">
      <button
        ref={setActivatorNodeRef}
        aria-label={`Drag handle for ${item.label}`}
        className="field-item-grip-btn"
        {...attributes}
        {...listeners}
        type="button"
        tabIndex={-1}
      >
        <MenuOutlined
          style={{
            touchAction: "none",
            cursor: "move",
          }}
        />
      </button>

      <p
        className="field-item-label"
        title={
          item.frozen
            ? "Drag to reorder within the frozen columns"
            : "Drag to reorder"
        }
      >
        {item.label}
      </p>

      <FrozenBadge frozen={item.frozen} />

      <Switch
        size="small"
        id={`sw-${item.id}`}
        checked={item.rank !== null}
        onChange={() => onToggle(item.id)}
        className="field-item-switch"
        disabled={COLUMN_MANAGER_LOCKED.includes(item.id) || item.frozen}
      />
    </div>
  );
});

export const HistorySelectedColumns = ({
  open,
  setOpen,
  selectedColumns,
  setSelectedColumns,
  frozenColumns = NO_FROZEN_COLUMNS,
  getRankingDataFun,
  defualtColumns,
  selectedMonth,
  messageToast,
}) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const scrollerRef = useRef(null);
  const dragStartScrollRef = useRef(0);

  useEffect(() => {
    if (open) {
      const allCols = checkboxData
        .filter((col) => col.value !== "all")
        .map((col) => {
          const colKey = col.value;
          const idx = selectedColumns.indexOf(colKey);
          const isVisible = idx !== -1;
          return {
            id: colKey,
            label: col.label,
            rank: isVisible ? idx + 1 : null,
            frozen: frozenColumns.includes(colKey),
          };
        })
        .sort((a, b) => {
          const orderA = a.rank !== null ? a.rank : Number.MAX_SAFE_INTEGER;
          const orderB = b.rank !== null ? b.rank : Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });

      // The grid renders frozen columns as a contiguous left block, so list
      // them first here too and renumber ranks to match what the grid shows.
      const shownCols = allCols.filter((col) => col.rank !== null);
      const hiddenCols = allCols.filter((col) => col.rank === null);
      const orderedShown = [
        ...shownCols.filter((col) => col.frozen),
        ...shownCols.filter((col) => !col.frozen),
      ].map((col, index) => ({ ...col, rank: index + 1 }));

      setItems([...orderedShown, ...hiddenCols]);
    }
  }, [open, selectedColumns, frozenColumns]);

  const shown = useMemo(
    () => items.filter((i) => i.rank !== null).sort((a, b) => a.rank - b.rank),
    [items],
  );
  const hidden = useMemo(() => items.filter((i) => i.rank === null), [items]);

  const shownIds = useMemo(() => shown.map((i) => i.id), [shown]);

  // Rows that keep their rank keep their object identity too, so the memoized
  // SortableItem can skip them on a reorder.
  const reassignRanks = (list) =>
    list.map((item, idx) =>
      item.rank === idx + 1 ? item : { ...item, rank: idx + 1 },
    );

  const activeItem = items.find((i) => i.id === activeId);

  function handleDragStart(event) {
    dragStartScrollRef.current = scrollerRef.current?.scrollTop ?? 0;
    setActiveId(event.active.id);
  }

  // A rejected drop leaves the row at its original slot, but auto-scroll has
  // usually carried the list away from it. The drag overlay is position: fixed
  // so nothing clips it, and the drop animation would tween up to that
  // off-screen slot, flying across the modal on the way. Restoring the scroll
  // position first puts the slot back on screen, so the animation plays out
  // inside the list. Must run synchronously here -- dnd-kit measures the
  // animation's endpoint in a layout effect straight after this.
  const restoreDragStartScroll = () => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = dragStartScrollRef.current;
    }
  };

  function handleDragCancel() {
    setActiveId(null);
    restoreDragStartScroll();
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveId(null);

    const oldIndex = shown.findIndex((i) => i.id === active.id);
    const newIndex = over ? shown.findIndex((i) => i.id === over.id) : -1;

    // Frozen columns are a contiguous block at the top: reorder freely within
    // either block, but never across the frozen / unfrozen boundary.
    const accepted =
      oldIndex !== -1 &&
      newIndex !== -1 &&
      oldIndex !== newIndex &&
      shown[oldIndex].frozen === shown[newIndex].frozen;

    if (!accepted) {
      restoreDragStartScroll();
      return;
    }

    const reordered = arrayMove(shown, oldIndex, newIndex);

    const updatedShown = reassignRanks(reordered);
    setItems([...updatedShown, ...hidden]);
  }

  // Stable identity so the memoized SortableItem isn't invalidated every render.
  const toggleItem = useCallback((id) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        if (i.rank !== null) return { ...i, rank: null };
        const maxRank = Math.max(0, ...prev.map((j) => j.rank || 0));
        return { ...i, rank: maxRank + 1 };
      }),
    );
  }, []);

  function applyFun() {
    // Every column is listed in the manager, so the shown rows are the whole
    // selection. The result is persisted by the grid preferences save this
    // state change triggers in the parent — the API is the only store.
    setSelectedColumns(shown.map((i) => i.id));
    // Matches the Rankings column manager: confirm on Apply for a consistent
    // experience across grids.
    messageToast?.("success", "Column configuration updated successfully.");
    setOpen(false);
    getRankingDataFun(1, 10);
  }


  return (
    <Modal
      open={open}
      okText="Apply"
      cancelText="Clear All"
      onCancel={() => setOpen(false)}
      onOk={applyFun}
      style={{
        minWidth: "800px",
      }}
      footer={[
        <Button key="back" type="text" onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={applyFun}
        >
          Apply
        </Button>,
      ]}
    >
      <div style={{ padding: "25px" }}>
        <div
          style={{ marginBottom: "20px", borderBottom: "solid 1px #80808047" }}
        >
          <Title level={5}>Add Columns</Title>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "20px",
          }}
        >
          <div>
            <p className="items-section-title">Shown</p>
            <div className="fields-overlay-drag-container" ref={scrollerRef}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
                onDragEnd={handleDragEnd}
                modifiers={[
                  restrictToVerticalAxis,
                  restrictToFirstScrollableAncestor,
                ]}
              >
                <SortableContext
                  items={shownIds}
                  strategy={verticalListSortingStrategy}
                >
                  <div>
                    {shown.map((item) => (
                      <SortableItem
                        key={item.id}
                        item={item}
                        onToggle={toggleItem}
                      />
                    ))}
                    {shown.length === 0 && (
                      <p className="empty-item">
                        No enabled items. Toggle some back on below.
                      </p>
                    )}
                  </div>
                </SortableContext>

                <DragOverlay>
                  {activeItem ? (
                    <SortableRow item={activeItem} onToggle={toggleItem} />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>

          <div>
            <p className="items-section-title">Hidden</p>
            <div className="fields-overlay-drag-container">
              {hidden.length ? (
                hidden.map((item) => (
                  <div key={item.id} className="field-item">
                    <div className="field-item-label">{item.label}</div>
                    <Switch
                      size="small"
                      id={`sw-${item.id}`}
                      checked={item.rank !== null}
                      onChange={() => toggleItem(item.id)}
                      className="field-item-switch"
                      disabled={COLUMN_MANAGER_LOCKED.includes(item.id) || item.frozen}
                    />
                  </div>
                ))
              ) : (
                <p className="empty-item">No hidden items.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
